/**
 * Which 401s mean "that credential is wrong" rather than "your session ended".
 *
 * Misty signs a person out on a 401, which is right for a token that has run
 * out and wrong for the handful of calls where the person is typing a
 * credential IN. A wrong password, a wrong six-digit code and — added in the
 * WP-BG review round, 18/09/2026 — a wrong manager PIN all answer 401, and the
 * interceptor used to treat the PIN one as an expired session: it silently sent
 * the same wrong PIN a second time (two of the five tries gone on one typo) and
 * then threw the manager out of Misty altogether, back to the login screen, in
 * the middle of the board. The PIN has its own lock for exactly that reason.
 *
 * Kept in its own file with nothing else in it so the rule can be tested on its
 * own; `utils/api.js` is a hot path that pulls in axios and cannot be.
 */

// Matched anywhere in the address: these have no look-alikes.
export const CREDENTIAL_401_PATHS = [
  '/auth/login',
  '/auth/refresh',
];

// Matched WHOLE. Three of the five run-group addresses carry a PIN; the other
// two — moving a group, and reading what is in one — carry no credential at
// all, so the only 401 either of them can produce is a session that has run
// out. Round one matched the addresses by prefix, which swallowed those two:
// an expired token on a group move was neither refreshed and replayed nor
// signed out, and the person was left on a dead session being told the move
// itself had been refused.
export const CREDENTIAL_401_PATTERNS = [
  /\/production\/board-groups$/,
  /\/production\/board-groups\/[^/]+\/members$/,
  /\/production\/board-groups\/[^/]+\/ungroup$/,
];

// Matched at the END of the address only. The other five two-factor routes
// (/auth/2fa/setup, verify-and-enable, disable, dismiss-setup,
// regenerate-recovery-codes) are signed-in calls, so a 401 on one of those IS
// the session ending and must sign the person out.
export const CREDENTIAL_401_ENDINGS = ['/auth/2fa/verify', '/auth/2fa/verify-recovery'];

export const isCredentialCall = (url) => {
  const path = String(url || '').split('?')[0];
  if (!path) return false;
  return CREDENTIAL_401_PATHS.some((p) => path.includes(p))
    || CREDENTIAL_401_ENDINGS.some((p) => path.endsWith(p))
    || CREDENTIAL_401_PATTERNS.some((p) => p.test(path));
};
